export default function Document() {
    return (
        <div id='doc'>
            <h3>🧩 Handlebars Helper Functions Documentation</h3>
            <p>
                This documentation lists all available custom Handlebars helpers, their purpose,
                syntax, and examples. These helpers are registered via the
                <code>registerHandlebarsHelpers()</code> function.
            </p>

            <hr />
            {/* 1. each */}
            <h4>1. <strong>#each</strong></h4>
            <p><strong>Description:</strong> Iterates over an array or object and renders its block for each element.</p>
            <p><strong>Syntax:</strong></p>
            <pre><code className="language-hbs">{'{{#each array}} ... {{/each}}'}</code></pre>
            <p><strong>Examples:</strong></p>
            <pre><code className="language-hbs">{'{{#each users}}<p>{{this.name}}</p>{{/each}}'}</code></pre>
            <pre><code className="language-hbs">{'{{#each (limit posts 3)}}<h4>{{title}}</h4>{{/each}}'}</code></pre>
            <p><strong>Notes:</strong> Within <code>each</code> loops, you can use <code>@index</code> to get the current index.</p>

            <hr />

            {/* 2. eq */}
            <h4>2. <strong>#eq</strong></h4>
            <p><strong>Description:</strong> Compares two values and renders the block if equal.</p>
            <pre><code className="language-hbs">{'{{#eq value1 value2}} ... {{/eq}}'}</code></pre>
            <pre><code className="language-hbs">{'{{#eq user.role "admin"}}Admin Access{{/eq}}'}</code></pre>

            <hr />

            {/* 3. if */}
            <h4>3. <strong>#if</strong></h4>
            <p><strong>Description:</strong> Performs comparison between two values using an operator (==, &lt;, &gt;, etc.).</p>
            <pre><code className="language-hbs">{'{{#if value ">" 10}} Greater than 10 {{else}} Less or equal {{/if}}'}</code></pre>

            <hr />

            {/* 4. date */}
            <h4>4. <strong>date</strong></h4>
            <p><strong>Description:</strong> Formats a date string using <code>moment.js</code>.</p>
            <pre><code className="language-hbs">{'{{ date dateString "DD MMM YYYY" }}'}</code></pre>
            <pre><code className="language-hbs">{'{{ date user.createdAt "DD MMM YYYY" }}'}</code></pre>

            <hr />

            {/* 5. year */}
            <h4>5. <strong>year</strong></h4>
            <p><strong>Description:</strong> Returns the current year.</p>
            <pre><code className="language-hbs">{'{{ year }}'}</code></pre>
            <p><strong>Output:</strong> <code>2025</code></p>

            <hr />

            {/* 6. or */}
            <h4>6. <strong>or</strong></h4>
            <p><strong>Description:</strong> Returns the first non-null or non-empty value.</p>
            <pre><code className="language-hbs">{'{{ or value1 value2 }}'}</code></pre>
            <pre><code className="language-hbs">{'{{ or user.name "Guest" }}'}</code></pre>

            <hr />

            {/* 7. limit */}
            <h4>7. <strong>#limit</strong></h4>
            <p><strong>Description:</strong> Limits the number of items rendered from an array.</p>
            <pre><code className="language-hbs">{'{{#limit array count}} ... {{/limit}}'}</code></pre>
            <pre><code className="language-hbs">{'{{#limit posts 3}}<h4>{{title}}</h4>{{/limit}}'}</code></pre>

            <hr />

            {/* 8. rawHTML */}
            <h4>8. <strong>rawHTML</strong></h4>
            <p><strong>Description:</strong> Safely renders raw HTML.</p>
            <pre><code className="language-hbs">{'{{{ rawHTML htmlString }}}'}</code></pre>
            <pre><code className="language-hbs">{'{{{ rawHTML "<b>Hello</b>" }}}'}</code></pre>

            <hr />

            {/* 9. deepIf */}
            <h4>9. <strong>#deepIf</strong></h4>
            <p><strong>Description:</strong> Evaluates complex JS expressions dynamically (e.g., <code>a &amp;&amp; b</code>).</p>
            <pre><code className="language-hbs">{'{{#deepIf "user.isAdmin && user.isActive"}}Access{{/deepIf}}'}</code></pre>

            <hr />

            {/* 10. renderMedia */}
            <h4>10. <strong>renderMedia</strong></h4>
            <p><strong>Description:</strong> Renders an <code>&lt;img&gt;</code> or <code>&lt;video&gt;</code> tag based on file type.</p>
            <pre><code className="language-hbs">{'{{ renderMedia imageSrc "rounded-md shadow-md" autoplay=true controls=true }}'}</code></pre>
            <p>- For condition based classes:</p>
            <pre><code className="language-hbs">{'{{renderMedia imageSrc (concat "w-full rounded-[" (or radius "0") "]")}}'}</code></pre>
            <p><strong>Output:</strong> Automatically detects if file is video or image.</p>

            <hr />

            {/* 11. concat */}
            <h4>11. <strong>concat</strong></h4>
            <p><strong>Description:</strong> Concatenates multiple strings together.</p>
            <pre><code className="language-hbs">{'{{ concat "Hello, " user.name "!" }}'}</code></pre>

            <hr />

            {/* 12. addOne */}
            <h4>12. <strong>addOne</strong></h4>
            <p><strong>Description:</strong> Adds <strong>1</strong> to the given number (useful for loops).</p>
            <pre><code className="language-hbs">{'{{ addOne @index }}'}</code></pre>
            <p><strong>Example:</strong></p>
            <pre><code className="language-hbs">{'name="option{{addOne index}}"'}</code></pre>
            <p><strong>Output:</strong></p>
            <pre><code className="language-hbs">{'name="option1"'}</code></pre>

            <hr />

            {/* 13. filterBy */}
            <h4>13. <strong>#filterBy</strong></h4>
            <p><strong>Description:</strong> Filters an array by key and value, rendering matching items.</p>
            <pre><code className="language-hbs">{'{{#filterBy users "role" "admin"}} {{name}} {{/filterBy}}'}</code></pre>

            <hr />

            {/* 14. sortBy */}
            <h4>14. <strong>#sortBy</strong></h4>
            <p><strong>Description:</strong> Sorts an array by a property (numeric, string, or date).</p>
            <pre><code className="language-hbs">{'{{#sortBy users "age"}} {{name}} - {{age}} {{/sortBy}}'}</code></pre>

            <hr />

            {/* 15. fetch */}
            <h4>15. <strong>fetch</strong></h4>
            <p><strong>Description:</strong> Fetches and renders an HTML block from an array by key/value match.</p>
            <pre><code className="language-hbs">{'{{ fetch blocks "id" 3 }}'}</code></pre>

            <hr />

            {/* 16. sortByAndFilter */}
            <h4>16. <strong>#sortByAndFilter</strong></h4>
            <p><strong>Description:</strong> Combines filtering and sorting operations in one call.</p>
            <pre><code className="language-hbs">{'{{#sortByAndFilter users "name" "role" "admin"}} {{name}} {{/sortByAndFilter}}'}</code></pre>
            <hr className='mb-5' />

            {/* ../ (Parent Context Access) */}
            <h3>⚙️ <strong>../ (Parent Context Access)</strong></h3>
            <p>
                The <code>../</code> syntax in Handlebars allows you to access data from a <strong>parent context </strong>
                when you’re inside nested blocks like <code>#each</code> or <code>#if</code>.
                It “goes up” one level in the context stack.
            </p>

            <p><strong>Syntax:</strong></p>
            <pre><code className="language-hbs">{'{{ ../parentKey }}'}</code></pre>

            <p><strong>Example:</strong></p>
            <pre><code className="language-hbs">{`{{#each section.header_nav}}
  <a href="{{link}}" style="color: {{../section.settings.header_text_color}}">
    {{label}}
  </a>
{{/each}}`}
            </code></pre>

            <p><strong>Use Case in Nested Each Loops:</strong></p>
            <pre><code className="language-hbs">{`{{#each section.header_nav}}
  {{#each items}}
    <a href="{{link}}" className="text-[{{or ../section.settings.header_text_size "14px"}}]">
      {{label}}
    </a>
  {{/each}}
{{/each}}`}
            </code></pre>

            <p><strong>Explanation:</strong></p>
            <ul>
                <li><code>{'{{ this}}'}</code> → refers to the current item in the innermost loop.</li>
                <li><code>{'{{../}}'}</code> → moves one level up to access the parent context.</li>
                <li>You can chain it: <code>{'{{../../}}'}</code> goes up two levels, and so on.</li>
            </ul>

            <p>
                <strong>In Your Example:</strong>
                <code>{'{{ "../section.settings.header_color"}}'}</code> is used inside a nested <code>#each</code> block to access
                the <code>section.settings</code> object from the parent scope.
            </p>
        </div>
    )
}
